import { AnyAction, Dispatch, Middleware, MiddlewareAPI } from 'redux';
import { Action, ActionCreator } from 'typescript-fsa';

export interface MiddlewareHandlerArgs<
  TState,
  TContext,
  TAction extends Action<unknown>
> {
  payload: TAction['payload'];
  dispatch: Dispatch;
  state: TState;
  context: TContext;
  action: TAction;
}

export interface MiddlewareHandler<
  TState,
  TContext,
  TAction extends Action<any>
> {
  /**
   * If it returns an action, that action will be automatically
   * dispatched.
   * @param P type of payload
   * @param S type of state
   * @param C type of context
   * @param A type of action
   */
  (
    handlerArgs: MiddlewareHandlerArgs<TState, TContext, TAction>
  ): void;
}

export type HandlerMap<
  TContext,
  TState,
> = {
  [actionType: string]: MiddlewareHandler<TState, TContext, Action<any>>;
};

const processMap = <TState, TAction extends Action<unknown>, TContext>(
    map: HandlerMap<TContext, TState>,
    store: MiddlewareAPI<Dispatch, TState>,
    action: TAction,
    context: TContext
) => {
    const handler = map[action.type];
    if (handler) {
        handler({
            payload: action.payload,
            dispatch: store.dispatch,
            state: store.getState(),
            action,
            context,
        });
    }
};

type Selector<R, S> = (state: S) => R;
type MonitoredHandler<S, C, R> = (p: {
    state: S;
    dispatch: Dispatch;
    context: C;
    current: R;
    previous: R;
    action: Action<unknown>
}) => void;

export type GenericMonitor<
  R extends (...args: unknown[]) => unknown,
  TState,
  TContext
> = MonitoredHandler<TState, TContext, ReturnType<R>>;

type CheckMonitored<S extends MiddlewareAPI<Dispatch>, C, P = unknown> = (
  store: S,
  context: C,
  action: Action<P>
) => void;

const monitor = <R, TContext, TState, SR extends MiddlewareAPI<Dispatch, TState>, P>(
    selector: Selector<R, TState>,
    handle: MonitoredHandler<TState, TContext, R>
): CheckMonitored<SR, TContext, P> => {
    let last: R;
    return (store: SR, context: TContext, action: Action<P>) => {
        const state = store.getState();
        const current = selector(state);
        if (current !== last) {
            const { dispatch } = store;
            handle({
                action,
                state,
                context,
                current: current,
                previous: last,
                dispatch,
            });
            last = current;
        }
    };
};

export type GetContext<TContext> = () => TContext;

export const genericCreateMiddleware = <
  TState,
  TContext,
  TStore extends MiddlewareAPI<Dispatch, TState>
>(
  getContext: GetContext<TContext>
) => {
  // "post" = after reducers have processed the action
  const postMap = {} as HandlerMap<TContext, TState>;
  const preMap = {} as HandlerMap<TContext, TState>;
  const monitored: CheckMonitored<TStore, TContext>[] = [];

  const obj = {
    /** Listen for an action *after* reducers have processed it */
    on: <P>(
      action: ActionCreator<P>,
      handler: MiddlewareHandler<TState, TContext, Action<P>>
    ) => {
      postMap[action.type] = handler;
      return obj;
    },
    onAny: <P>(
      actions: ActionCreator<P>[],
      handler: MiddlewareHandler<TState, TContext, Action<P>>
    ) => {
      actions.forEach((action) => (postMap[action.type] = handler));
      return obj;
    },
    onAnyType: <P>(
      actionTypes: string[],
      handler: MiddlewareHandler<TState, TContext, Action<P>>
    ) => {
      actionTypes.forEach((actionType) => (postMap[actionType] = handler));
      return obj;
    },
    onChange: <R>(
      selector: Selector<R, TState>,
      handler: MonitoredHandler<TState, TContext, R>
    ) => {
      monitored.push(monitor(selector, handler));
      return obj;
    },
    /** Listen for an action *before* reducers have processed it */
    pre: <P>(
      action: ActionCreator<P>,
      handler: MiddlewareHandler<TState, TContext, Action<P>>
    ) => {
      preMap[action.type] = handler;
      return obj;
    },
    step: (store: TStore, next: Dispatch, action: Action<AnyAction>) => {
      const context = getContext && getContext();
      processMap(preMap, store, action, context);
      // Call the next dispatch method in the middleware chain.
      const returnValue = next(action);
      monitored.forEach((handler) => handler(store, context, action));
      processMap(postMap, store, action, context);
      return returnValue;
    },
    build: (): Middleware => (store: MiddlewareAPI<Dispatch, TState>) => (next) => (action) => {
      return obj.step(store as TStore, next, action);
    },
  };
  return obj;
};

export type MiddlewareCondition = <TContext, TState>(
  state: TState,
  action: Action<AnyAction>,
  context: TContext
) => boolean;

interface MozakCompositeMiddleware {
    step: <TStore>(
        store: TStore,
        next: Dispatch,
        action: Action<AnyAction>
    ) => Action<AnyAction>;
}

interface CombinerMap {
    condition: MiddlewareCondition;
    onTrue: MozakCompositeMiddleware;
    onFalse?: MozakCompositeMiddleware;
}

/**
 * Combine multiple middlewares created using `createMiddleware` into one.
 * Flow will be dynamically rerouted depending on the specified conditions
 * @example
 *  const isInNormalMode: MiddlewareCondition = (state: State) =>
 *      state.score.goal.passScore > 0;
 *
 *  export const scoreMiddleware = (ctx: GetContext) =>
 *      combineMiddleware(ctx)
 *          .when(isInNormalMode, normalModeMiddleware, tutorialModeMiddleware)
 *          .build();
 */
export const combineMiddleware = <TContext>(getContext: GetContext<TContext>) => {
    const switchMap: CombinerMap[] = [];
    const obj = {
        /**
         * @param condition (state: State, action: Action<AnyAction>, context: Context): boolean;
         */
        when: (
            condition: MiddlewareCondition,
            onTrue: (ctx: GetContext<TContext>) => MozakCompositeMiddleware,
            onFalse?: (ctx: GetContext<TContext>) => MozakCompositeMiddleware
        ) => {
            switchMap.push({
                condition,
                onTrue: onTrue(getContext),
                onFalse: onFalse?.(getContext),
            });
            return obj;
        },
        build: (): Middleware => (store: MiddlewareAPI<Dispatch>) => (next) => (action) => {
            const context = getContext && getContext();
            let chainedAction = action;
            switchMap.forEach(({ condition, onFalse, onTrue }) => {
                const state = store.getState();
                chainedAction = condition(state, action, context)
                    ? onTrue.step(store, next, chainedAction)
                    : onFalse
                    ? onFalse.step(store, next, chainedAction)
                    : next(chainedAction);
            });
            return chainedAction;
        },
    };
    return obj;
};
